import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnalysisJobResult } from '../@types/background-jobs';
import { useGetAIMetadataQuery } from '../redux/services/ai-api';
import { useGetAnalysisJobStatusQuery, useStartSingleRecordAnalysisJobMutation } from '../redux/services/record';
import { showError } from '../services/notification.service';

type AnalysisResultWithTimestamp = AnalysisJobResult & { timestamp?: number };

export const useAnalysisBackgroundJobHandler = () => {
  const { t } = useTranslation();
  const { data: aiMetadata } = useGetAIMetadataQuery();
  const [triggerStartSingleRecordAnalysisJob, { isLoading: isStartingAnalysisJob }] =
    useStartSingleRecordAnalysisJobMutation();

  const [analysisResult, setAnalysisResult] = useState<AnalysisResultWithTimestamp | undefined>();
  const [isStartingAnalysisJobError, setIsStartingAnalysisJobError] = useState(false);
  const [jobId, setJobId] = useState<number | undefined>(undefined);
  const [isOutdated, setIsOutdated] = useState(false);

  const { data: { status, result } = {}, isError } = useGetAnalysisJobStatusQuery(jobId!, {
    skip: !jobId,
    pollingInterval: jobId ? 5000 : 0, // 5 seconds polling interval
  });

  const startAIAnalysis = useCallback((recordId: number) => {
    setIsStartingAnalysisJobError(false);
    triggerStartSingleRecordAnalysisJob(recordId)
      .then((res) => {
        if (res?.data?.backgroundJobId) {
          setJobId(res.data.backgroundJobId);
          return;
        }
        setIsStartingAnalysisJobError(true);
      })
      .catch(() => {
        setIsStartingAnalysisJobError(true);
      });
  }, []);

  // reset analysis result when jobId changes
  useEffect(() => {
    if (jobId) {
      setAnalysisResult(undefined);
      setIsOutdated(false);
    }
  }, [jobId]);

  useEffect(() => {
    if (status === 'FINISHED') {
      const timestamp = Date.now();
      setAnalysisResult({ ...result, timestamp });
      setJobId(undefined);
    } else if (status === 'FAILED' || isError) {
      setJobId(undefined);
      showError(t('analysis-job-failed', 'The analysis job has failed. Please try again.'));
    }
  }, [status, isError, result]);

  return {
    analysisResult,
    isError: status === 'FAILED' || isError || isStartingAnalysisJobError,
    isLoading: !!jobId || isStartingAnalysisJob,
    timestamp: analysisResult?.timestamp,
    isOutdated,
    setIsOutdated,
    showAnalyzeIcon: aiMetadata && aiMetadata.find((item) => item.key === 'record_ai_enabled')?.value === 'true',
    startAIAnalysis,
  };
};
